/**
 * Analytics Service Layer
 * Unified interface for Plausible + PostHog with consent management
 */

import type { CookieConsent } from '@/contexts/CookieConsentContext';

interface QueuedEvent {
  name: string;
  properties?: Record<string, unknown>;
  timestamp: number;
}

interface PlausibleOptions {
  callback?: () => void;
  props?: Record<string, string | number | boolean>;
}

declare global {
  interface Window {
    plausible?: (event: string, options?: PlausibleOptions) => void;
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
      identify: (userId: string, properties?: Record<string, unknown>) => void;
      reset: () => void;
      opt_out_capturing: () => void;
      opt_in_capturing: () => void;
      isFeatureEnabled: (flag: string) => boolean;
      getFeatureFlag: (flag: string) => string | boolean | undefined;
      onFeatureFlags: (callback: () => void) => void;
    };
  }
}

class AnalyticsService {
  private eventQueue: QueuedEvent[] = [];
  private consent: CookieConsent | null = null;
  private initialized = false;

  /**
   * Initialize analytics with current consent state
   */
  init(consent: CookieConsent): void {
    this.consent = consent;
    this.initialized = true;
    
    // Plausible is always active (cookie-free)
    // PostHog only when analytics consent is given
    if (consent.analytics) {
      this.flushEventQueue();
    }
  }

  /**
   * Update consent and handle provider state changes
   */
  onConsentChange(newConsent: CookieConsent): void {
    const previousAnalytics = this.consent?.analytics;
    this.consent = newConsent;

    if (newConsent.analytics && !previousAnalytics) {
      // Analytics just enabled - flush queued events
      this.flushEventQueue();
      window.posthog?.opt_in_capturing();
    } else if (!newConsent.analytics && previousAnalytics) {
      // Analytics just disabled
      window.posthog?.opt_out_capturing();
    }
  }

  /**
   * Check if analytics tracking is allowed
   */
  private canTrackAnalytics(): boolean {
    return this.consent?.analytics === true;
  }

  /**
   * Track a page view
   */
  trackPageView(url?: string): void {
    const pageUrl = url || window.location.pathname;
    
    // Plausible - always active (cookie-free)
    if (window.plausible) {
      window.plausible('pageview');
    }

    // PostHog - only with consent
    if (this.canTrackAnalytics() && window.posthog) {
      window.posthog.capture('$pageview', { 
        $current_url: window.location.href,
        path: pageUrl 
      });
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(name: string, properties?: Record<string, unknown>): void {
    // Plausible - always active (limited properties)
    if (window.plausible) {
      const plausibleProps = properties 
        ? Object.fromEntries(
            Object.entries(properties).map(([k, v]) => [k, String(v)])
          ) as Record<string, string | number | boolean>
        : undefined;
      window.plausible(name, { props: plausibleProps });
    }

    // PostHog - only with consent
    if (this.canTrackAnalytics() && window.posthog) {
      window.posthog.capture(name, properties);
    } else if (!this.canTrackAnalytics()) {
      // Queue event for later if no consent yet
      this.queueEvent(name, properties);
    }
  }

  /**
   * Track CTA button clicks
   */
  trackCTAClick(buttonName: string, location?: string): void {
    this.trackEvent('cta_click', {
      button: buttonName,
      location: location || 'unknown',
      url: window.location.pathname
    });
  }

  /**
   * Track scroll depth
   */
  trackScrollDepth(depth: number): void {
    this.trackEvent('scroll_depth', {
      depth: `${depth}%`,
      url: window.location.pathname
    });
  }

  /**
   * Track form submission
   */
  trackFormSubmission(formName: string, success: boolean): void {
    this.trackEvent('form_submission', {
      form: formName,
      success,
      url: window.location.pathname
    });
  }

  /**
   * Track form abandonment
   */
  trackFormAbandonment(formName: string, filledFields: string[]): void {
    this.trackEvent('form_abandonment', {
      form: formName,
      fields_filled: filledFields.join(','),
      field_count: filledFields.length,
      url: window.location.pathname
    });
  }

  /**
   * Track time on page
   */
  trackTimeOnPage(seconds: number): void {
    this.trackEvent('time_on_page', {
      seconds,
      url: window.location.pathname
    });
  }

  /**
   * Track outbound link clicks
   */
  trackOutboundLink(url: string, text?: string): void {
    this.trackEvent('outbound_link', {
      destination: url,
      text: text || 'unknown',
      source: window.location.pathname
    });
  }

  /**
   * Track Web Vitals
   */
  trackWebVital(name: string, value: number, rating: string): void {
    this.trackEvent('web_vital', {
      metric: name,
      value: Math.round(value),
      rating
    });
  }

  /**
   * Identify user (PostHog only)
   */
  identify(userId: string, properties?: Record<string, unknown>): void {
    if (this.canTrackAnalytics() && window.posthog) {
      window.posthog.identify(userId, properties);
    }
  }

  /**
   * Reset user identity
   */
  reset(): void {
    if (window.posthog) {
      window.posthog.reset();
    }
  }

  /**
   * Check if a feature flag is enabled (PostHog)
   */
  isFeatureEnabled(flag: string): boolean {
    if (this.canTrackAnalytics() && window.posthog) {
      return window.posthog.isFeatureEnabled(flag);
    }
    return false;
  }

  /**
   * Get feature flag value (PostHog)
   */
  getFeatureFlag(flag: string): string | boolean | undefined {
    if (this.canTrackAnalytics() && window.posthog) {
      return window.posthog.getFeatureFlag(flag);
    }
    return undefined;
  }

  /**
   * Queue an event for later when consent is given
   */
  private queueEvent(name: string, properties?: Record<string, unknown>): void {
    this.eventQueue.push({
      name,
      properties,
      timestamp: Date.now()
    });

    // Limit queue size
    if (this.eventQueue.length > 100) {
      this.eventQueue.shift();
    }
  }

  /**
   * Flush queued events when consent is given
   */
  private flushEventQueue(): void {
    if (!this.canTrackAnalytics() || !window.posthog) {return;}

    const events = [...this.eventQueue];
    this.eventQueue = [];

    events.forEach(event => {
      window.posthog?.capture(event.name, {
        ...event.properties,
        queued: true,
        original_timestamp: event.timestamp
      });
    });
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();
